import { EMPTY, formatDateFull, formatInteger, formatPercent, formatTime, isNumber, toInstant } from './piFormat.js';
import { classifyError } from './piEnvelope.js';
import { MAX_RANGE_DAYS, addDays, daysBetween, parseIsoDate, todayIso } from './piQuery.js';
import { PAGE_SIZES } from '../default/pagination.js';
import { MAX_DECIMALS, defineColumns } from './piColumns.js';
import {
  AGENT_LABELS, AGENT_ORDER, DEFAULT_AGENT, STATUS_INFO, agentLabel, cleanText, costPair, feedbackInfo, formatMoney, formatMoneyExact, inlineSegments,
  isGuid, latencyText, normalizeBody, normalizeTrace, objectOf, routeText, stampText, statusInfo, toolStatusInfo, warningItems,
} from './piAssistantModel.js';

export const USAGE_PRESETS = [
  { value: '7', days: 7, label: '7 dias' },
  { value: '30', days: 30, label: '30 dias' },
  { value: '90', days: 90, label: '90 dias' },
];

export const DEFAULT_USAGE_PRESET = '30';

export const USAGE_PAGE_SIZE = 50;

export const USAGE_MAX_PAGE_SIZE = 200;

export const USAGE_SEARCH_MAX = 60;

export const USAGE_QUESTION_PREVIEW = 120;

export const USAGE_HEADLINE_PREVIEW = 160;

export const USAGE_AGENT_KEYS = AGENT_ORDER.filter((key) => key !== DEFAULT_AGENT);

export const REVENUE_AGENT = 'revenue';

export const USAGE_STORAGE_KEY = 'filters:pi-assistant-usage';

export const USAGE_QUERY = {
  preset: 'periodo',
  from: 'de',
  to: 'ate',
  userId: 'pessoa',
  agent: 'agente',
  status: 'situacao',
  search: 'busca',
  sort: 'ordem',
  ascending: 'crescente',
  page: 'pagina',
  pageSize: 'itens',
};

const USAGE_QUERY_KEYS = Object.keys(USAGE_QUERY).map((key) => USAGE_QUERY[key]);

export const USAGE_FILTER_DEFAULTS = { preset: DEFAULT_USAGE_PRESET, from: '', to: '', userId: '', agent: '', status: '', search: '' };

export const DEFAULT_USAGE_SORT = { sort: 'askedAt', desc: true };

export const USAGE_SORT_KEYS = ['askedAt', 'costUsd', 'latencyMs', 'totalTokens'];

export const USAGE_SCREEN = 'Consumo da IA';

export const USAGE_PEOPLE_CONFIRM = 'A planilha traz o nome e o e-mail dos usuários do Master que fizeram as perguntas. ' +
  'Compartilhe só com quem pode ver esses dados. Exportar?';

export const PURPOSE_LABELS = { router: 'Roteador', answer: 'Resposta' };

export const FINISH_LABELS = {
  stop: 'terminou',
  tool_calls: 'pediu ferramentas',
  length: 'cortada pelo limite de tokens',
  content_filter: 'bloqueada pelo filtro de conteúdo',
};

const listOf = (value) => (Array.isArray(value) ? value : []);

const numberOr = (value, fallback) => (isNumber(value) ? value : fallback);

const secondsOf = (value) => (isNumber(value) ? Math.round(value) / 1000 : null);

const senderName = (value) => cleanText(value, 200) || '(sem nome)';

const finishText = (value) => {
  const finish = cleanText(value, 30);
  if (!finish) return '';
  return FINISH_LABELS[finish] ? FINISH_LABELS[finish] + ' (' + finish + ')' : finish;
};

const jsonText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch (e) {
    return '';
  }
};

const flagValue = (value) => (value === true || value === false ? value : '');

const COST_DECIMALS = MAX_DECIMALS;

export const USAGE_DAY_COLUMNS = defineColumns([
  { key: 'date', label: 'Dia', type: 'date', infoKey: 'assistant.usage.day' },
  { key: 'questions', label: 'Perguntas', type: 'int', infoKey: 'assistant.usage.questions' },
  { key: 'users', label: 'Pessoas', type: 'int', infoKey: 'assistant.usage.users' },
  { key: 'inputTokens', label: 'Entrada', type: 'int', infoKey: 'assistant.usage.inputTokens' },
  { key: 'cachedInputTokens', label: 'Em cache', type: 'int', infoKey: 'assistant.usage.cachedTokens' },
  { key: 'outputTokens', label: 'Saída', type: 'int', infoKey: 'assistant.usage.outputTokens' },
  { key: 'costUsd', label: 'Custo', type: 'decimal', decimals: COST_DECIMALS, infoKey: 'assistant.usage.cost' },
  { key: 'costBrl', label: 'Custo (R$)', type: 'decimal', decimals: COST_DECIMALS, infoKey: 'assistant.usage.costBrl', screen: false },
]);

export const USAGE_USER_COLUMNS = defineColumns([
  { key: 'name', label: 'Pessoa', type: 'text', infoKey: 'assistant.usage.person', value: (row) => senderName(row.name) },
  { key: 'email', label: 'E-mail', type: 'text', infoKey: 'assistant.usage.email', screen: false },
  { key: 'questions', label: 'Perguntas', type: 'int', infoKey: 'assistant.usage.questions' },
  { key: 'costUsd', label: 'Custo', type: 'decimal', decimals: COST_DECIMALS, infoKey: 'assistant.usage.cost' },
  { key: 'costBrl', label: 'Custo (R$)', type: 'decimal', decimals: COST_DECIMALS, infoKey: 'assistant.usage.costBrl', screen: false },
  { key: 'avgCostUsd', label: 'Custo médio', type: 'decimal', decimals: COST_DECIMALS, infoKey: 'assistant.usage.avgCost' },
  { key: 'lastAskedAt', label: 'Última', type: 'datetime', infoKey: 'assistant.usage.lastAsked' },
  { key: 'masterUserId', label: 'Identificador no Master', type: 'text', infoKey: 'assistant.usage.userId', screen: false },
]);

export const USAGE_AGENT_COLUMNS = defineColumns([
  { key: 'agent', label: 'Agente usado', type: 'text', infoKey: 'assistant.usage.agentUsed', value: (row) => agentLabel(row.agent, row.label) },
  { key: 'questions', label: 'Perguntas', type: 'int', infoKey: 'assistant.usage.questions' },
  { key: 'costUsd', label: 'Custo', type: 'decimal', decimals: COST_DECIMALS, infoKey: 'assistant.usage.cost' },
  { key: 'avgCostUsd', label: 'Custo médio', type: 'decimal', decimals: COST_DECIMALS, infoKey: 'assistant.usage.avgCost' },
  { key: 'avgLatencyMs', label: 'Tempo médio', type: 'decimal', infoKey: 'assistant.usage.avgLatency', value: (row) => secondsOf(row.avgLatencyMs) },
  { key: 'usefulRate', label: 'Útil', type: 'percent', infoKey: 'assistant.usage.usefulRate' },
]);

export const USAGE_QUESTION_COLUMNS = defineColumns([
  { key: 'askedAt', label: 'Data e hora', type: 'datetime', infoKey: 'assistant.usage.askedAt', sortable: true, startDescending: true },
  { key: 'userName', label: 'Quem enviou', type: 'text', infoKey: 'assistant.usage.sender', value: (row) => senderName(row.userName) },
  { key: 'userEmail', label: 'E-mail', type: 'text', infoKey: 'assistant.usage.email', screen: false },
  { key: 'question', label: 'Pergunta', type: 'text', infoKey: 'assistant.usage.question', value: (row) => cleanText(row.question, 2000) },
  { key: 'agent', label: 'Agente', type: 'text', infoKey: 'assistant.usage.agentPath', export: false, exportedAs: ['agentRequested', 'agentUsed'] },
  {
    key: 'agentRequested', label: 'Agente escolhido', type: 'text', infoKey: 'assistant.usage.agentRequested', screen: false,
    value: (row) => (cleanText(row.agentRequested, 40) ? agentLabel(row.agentRequested) : ''),
  },
  { key: 'agentUsed', label: 'Agente usado', type: 'text', infoKey: 'assistant.usage.agentUsed', screen: false, value: (row) => agentLabel(row.agentUsed) },
  { key: 'status', label: 'Status', type: 'text', infoKey: 'assistant.answerStatus', value: (row) => statusInfo(row.status).label },
  { key: 'unverifiedNumbers', label: 'Números não conferidos', type: 'int', infoKey: 'assistant.unverifiedNumbers', screen: false },
  {
    key: 'totalTokens', label: 'Tokens', type: 'int', infoKey: 'assistant.usage.totalTokens', sortable: true, startDescending: true,
    value: (row) => numberOr(row.totalTokens, numberOr(row.inputTokens, 0) + numberOr(row.outputTokens, 0)),
  },
  { key: 'inputTokens', label: 'Tokens de entrada', type: 'int', infoKey: 'assistant.usage.inputTokens', screen: false },
  { key: 'cachedInputTokens', label: 'Em cache', type: 'int', infoKey: 'assistant.usage.cachedTokens', screen: false },
  { key: 'outputTokens', label: 'Tokens de saída', type: 'int', infoKey: 'assistant.usage.outputTokens', screen: false },
  { key: 'modelCalls', label: 'Chamadas ao modelo', type: 'int', infoKey: 'assistant.usage.modelCalls', screen: false },
  { key: 'toolCalls', label: 'Consultas ao PI', type: 'int', infoKey: 'assistant.usage.toolCalls', screen: false },
  { key: 'costUsd', label: 'Custo', type: 'decimal', decimals: COST_DECIMALS, infoKey: 'assistant.usage.cost', sortable: true, startDescending: true },
  { key: 'costBrl', label: 'Custo (R$)', type: 'decimal', decimals: COST_DECIMALS, infoKey: 'assistant.usage.costBrl', screen: false },
  {
    key: 'latencyMs', label: 'Tempo', type: 'decimal', infoKey: 'assistant.usage.latency', sortable: true, startDescending: true,
    value: (row) => secondsOf(row.latencyMs),
  },
  {
    key: 'feedback', label: 'Avaliação', type: 'text', infoKey: 'assistant.usage.feedback',
    value: (row) => (feedbackInfo(row.feedback) ? feedbackInfo(row.feedback).label : ''),
  },
  { key: 'interactionId', label: 'Número da pergunta', type: 'int', infoKey: 'assistant.usage.interactionId', screen: false },
]);

export const MODEL_CALL_COLUMNS = defineColumns([
  { key: 'index', label: '#', type: 'int', infoKey: 'assistant.call.index' },
  {
    key: 'purpose', label: 'Finalidade', type: 'text', infoKey: 'assistant.call.purpose',
    value: (row) => PURPOSE_LABELS[row.purpose] || cleanText(row.purpose, 20),
  },
  { key: 'model', label: 'Modelo', type: 'text', infoKey: 'assistant.call.model', screen: false },
  { key: 'promptHash', label: 'Prompt', type: 'text', infoKey: 'assistant.call.prompt', screen: false },
  { key: 'errorCode', label: 'Erro', type: 'text', infoKey: 'assistant.call.error', screen: false },
  { key: 'startedAt', label: 'Início', type: 'datetime', infoKey: 'assistant.call.startedAt', screen: false },
  { key: 'inputMessageCount', label: 'Mensagens', type: 'int', infoKey: 'assistant.call.messages' },
  { key: 'inputTokens', label: 'Entrada', type: 'int', infoKey: 'assistant.usage.inputTokens' },
  { key: 'cachedInputTokens', label: 'Em cache', type: 'int', infoKey: 'assistant.usage.cachedTokens' },
  { key: 'outputTokens', label: 'Saída', type: 'int', infoKey: 'assistant.usage.outputTokens' },
  { key: 'finishReason', label: 'Fim', type: 'text', infoKey: 'assistant.call.finish', value: (row) => finishText(row.finishReason) },
  { key: 'toolCallCount', label: 'Ferramentas', type: 'int', infoKey: 'assistant.call.tools' },
  { key: 'costUsd', label: 'Custo', type: 'decimal', decimals: COST_DECIMALS, infoKey: 'assistant.usage.cost' },
  { key: 'latencyMs', label: 'Tempo', type: 'decimal', infoKey: 'assistant.call.latency', value: (row) => secondsOf(row.latencyMs) },
]);

export const TOOL_CALL_COLUMNS = defineColumns([
  {
    key: 'call', label: 'Chamada', type: 'text', infoKey: 'assistant.tool.call',
    value: (row) => '#' + (isNumber(row.modelCallIndex) ? row.modelCallIndex : '?') + '.' + (isNumber(row.index) ? row.index : '?'),
  },
  { key: 'toolCallId', label: 'Identificador da chamada', type: 'text', infoKey: 'assistant.tool.callId', screen: false },
  { key: 'tool', label: 'Ferramenta', type: 'text', infoKey: 'assistant.tool.name' },
  { key: 'endpoint', label: 'Rota do PI', type: 'text', infoKey: 'assistant.tool.endpoint', screen: false },
  { key: 'asOf', label: 'Dados de', type: 'datetime', infoKey: 'common.asOf', screen: false },
  { key: 'isPartial', label: 'Parcial', type: 'text', infoKey: 'common.state.partial', screen: false, value: (row) => flagValue(row.isPartial) },
  { key: 'arguments', label: 'Argumentos', type: 'text', infoKey: 'assistant.tool.arguments', value: (row) => jsonText(row.arguments) },
  { key: 'status', label: 'Status', type: 'text', infoKey: 'assistant.tool.status', value: (row) => toolStatusInfo(row.status).label },
  { key: 'errorMessage', label: 'Erro', type: 'text', infoKey: 'assistant.tool.error', screen: false },
  { key: 'rows', label: 'Linhas', type: 'int', infoKey: 'assistant.tool.rows' },
  { key: 'resultChars', label: 'Caracteres', type: 'int', infoKey: 'assistant.tool.chars' },
  { key: 'resultTokensEstimate', label: 'Tokens (est.)', type: 'int', infoKey: 'assistant.tool.tokens' },
  { key: 'truncated', label: 'Cortado', type: 'text', infoKey: 'assistant.tool.truncated', value: (row) => flagValue(row.truncated) },
  { key: 'latencyMs', label: 'Tempo', type: 'decimal', infoKey: 'assistant.tool.latency', value: (row) => secondsOf(row.latencyMs) },
]);

export const TRANSCRIPT_ROLES = {
  system: 'Sistema',
  user: 'Pergunta',
  assistant: 'Modelo',
  tool: 'Resultado da ferramenta',
};

export const USAGE_LEAD = 'Cada pergunta feita ao assistente, com quem enviou, o que entrou e saiu de cada chamada ao modelo, as consultas ao PI, ' +
  'os tokens e o custo. Visível para os operadores da plataforma; a pergunta é gravada com CPF, e-mail e telefone mascarados.';

export const PURGED_TEXT = 'Transcrição apagada após 180 dias: ficam a pergunta, quem enviou, os tokens e o custo.';

export const NO_TRANSCRIPT_TEXT = 'Esta pergunta não tem transcrição gravada (recusa antes de chamar o modelo).';

const USER_ID = /^[0-9A-Za-z_-]{1,80}$/;

const presetOf = (value) => USAGE_PRESETS.filter((preset) => preset.value === value)[0] || null;

const shorten = (value, max) => {
  const text = cleanText(value, 4000).replace(/\s+/g, ' ');
  return text.length > max ? text.substring(0, max - 1) + '…' : text;
};

const plural = (count, one, many) => formatInteger(count) + ' ' + (count === 1 ? one : many);

export const usageRangeError = (from, to, today) => {
  if (!parseIsoDate(from) || !parseIsoDate(to)) return 'Informe as duas datas do período.';
  if (from > to) return 'A data inicial é depois da data final.';
  if (to > today) return 'A data final não pode ser depois de hoje.';
  if (daysBetween(from, to) > MAX_RANGE_DAYS) return 'O período máximo é de ' + MAX_RANGE_DAYS + ' dias.';
  return null;
};

export const usagePeriod = (filters, now) => {
  const source = objectOf(filters);
  const today = todayIso(now);
  const preset = presetOf(source.preset) || presetOf(DEFAULT_USAGE_PRESET);
  const fallback = { from: addDays(today, -(preset.days - 1)), to: today, days: preset.days, preset: preset.value, custom: false, error: null };
  if (!source.from && !source.to) return fallback;
  const error = usageRangeError(source.from, source.to, today);
  if (error) return Object.assign({}, fallback, { error: error });
  return { from: source.from, to: source.to, days: daysBetween(source.from, source.to) + 1, preset: 'custom', custom: true, error: null };
};

export const sanitizeUsageFilters = (filters) => {
  const source = objectOf(filters);
  const clean = Object.assign({}, USAGE_FILTER_DEFAULTS);
  if (presetOf(source.preset)) clean.preset = source.preset;
  if (parseIsoDate(source.from)) clean.from = source.from;
  if (parseIsoDate(source.to)) clean.to = source.to;
  if (typeof source.userId === 'string' && USER_ID.test(source.userId)) clean.userId = source.userId;
  if (USAGE_AGENT_KEYS.indexOf(source.agent) !== -1) clean.agent = source.agent;
  if (Object.prototype.hasOwnProperty.call(STATUS_INFO, source.status)) clean.status = source.status;
  clean.search = cleanText(source.search, USAGE_SEARCH_MAX).replace(/\s+/g, ' ');
  return clean;
};

export const usageFiltersActive = (filters) => {
  const clean = sanitizeUsageFilters(filters);
  return !!(clean.userId || clean.agent || clean.status || clean.search || clean.from || clean.to || clean.preset !== DEFAULT_USAGE_PRESET);
};

export const usageParams = (filters, extra, now) => {
  const clean = sanitizeUsageFilters(filters);
  const options = objectOf(extra);
  const period = usagePeriod(clean, now);
  const params = { from: period.from, to: period.to };
  if (clean.userId) params.userId = clean.userId;
  if (clean.agent) params.agent = clean.agent;
  if (clean.status) params.status = clean.status;
  if (clean.search) params.search = clean.search;
  const page = Math.floor(Number(options.page));
  const size = Math.floor(Number(options.pageSize));
  params.page = String(isFinite(page) && page >= 1 ? page : 1);
  params.pageSize = String(isFinite(size) && size >= 1 ? Math.min(size, USAGE_MAX_PAGE_SIZE) : USAGE_PAGE_SIZE);
  params.sort = USAGE_SORT_KEYS.indexOf(options.sort) !== -1 ? options.sort : DEFAULT_USAGE_SORT.sort;
  params.desc = options.desc === false ? 'false' : 'true';
  return params;
};

const optionText = (options, value) => {
  const found = listOf(options).filter((option) => option && option.value === value)[0];
  return found && found.label ? found.label : value;
};

export const usageFilterLines = (filters, options, now) => {
  const clean = sanitizeUsageFilters(filters);
  const settings = objectOf(options);
  const period = usagePeriod(clean, now);
  const lines = ['Período: ' + formatDateFull(period.from) + ' a ' + formatDateFull(period.to) + ', hoje incluído (dia de Brasília)'];
  if (clean.userId) lines.push('Pessoa: ' + optionText(settings.users, clean.userId));
  if (clean.agent) lines.push('Agente usado: ' + optionText(settings.agents, clean.agent));
  if (clean.status) lines.push('Status: ' + statusInfo(clean.status).label);
  if (clean.search) lines.push('Buscar na pergunta: "' + clean.search + '"');
  return lines;
};

export const usageSortLine = (sort) => {
  const source = objectOf(sort);
  const column = USAGE_QUESTION_COLUMNS.filter((spec) => spec.key === source.sort)[0] || USAGE_QUESTION_COLUMNS[0];
  return 'Ordem das perguntas: ' + column.label + (source.desc === false ? ', crescente' : ', decrescente');
};

export const usageExportPeriod = (filters, now) => {
  const period = usagePeriod(sanitizeUsageFilters(filters), now);
  return { from: period.from, to: period.to };
};

export const agentOptions = (agents) => {
  const loaded = Array.isArray(agents) ? agents.map(objectOf).filter((agent) => USAGE_AGENT_KEYS.indexOf(agent.key) !== -1) : [];
  if (loaded.length) return loaded.map((agent) => ({ value: agent.key, label: agentLabel(agent.key, agent.label) }));
  return USAGE_AGENT_KEYS.filter((key) => key !== REVENUE_AGENT).map((key) => ({ value: key, label: AGENT_LABELS[key] }));
};

const queryValue = (query, key) => {
  const raw = query[USAGE_QUERY[key]];
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (typeof value === 'string') return value.trim();
  return isNumber(value) ? String(value) : '';
};

const positiveInt = (value) => (/^\d{1,6}$/.test(value) ? Number(value) : 0);

export const hasUsageQuery = (query) => {
  const source = objectOf(query);
  return USAGE_QUERY_KEYS.some((key) => Object.prototype.hasOwnProperty.call(source, key));
};

export const usageStateFromQuery = (query) => {
  const source = objectOf(query);
  const filters = sanitizeUsageFilters({
    preset: queryValue(source, 'preset'),
    from: queryValue(source, 'from'),
    to: queryValue(source, 'to'),
    userId: queryValue(source, 'userId'),
    agent: queryValue(source, 'agent'),
    status: queryValue(source, 'status'),
    search: queryValue(source, 'search'),
  });
  const sort = queryValue(source, 'sort');
  const page = positiveInt(queryValue(source, 'page'));
  const pageSize = positiveInt(queryValue(source, 'pageSize'));
  return {
    filters: filters,
    sort: { sort: USAGE_SORT_KEYS.indexOf(sort) !== -1 ? sort : DEFAULT_USAGE_SORT.sort, desc: queryValue(source, 'ascending') !== '1' },
    page: page >= 1 ? page : 1,
    pageSize: PAGE_SIZES.indexOf(pageSize) !== -1 ? pageSize : USAGE_PAGE_SIZE,
  };
};

export const usageStateToQuery = (state) => {
  const source = objectOf(state);
  const filters = sanitizeUsageFilters(source.filters);
  const sort = objectOf(source.sort);
  const query = {};
  if (filters.preset !== DEFAULT_USAGE_PRESET) query[USAGE_QUERY.preset] = filters.preset;
  ['from', 'to', 'userId', 'agent', 'status', 'search'].forEach((key) => {
    if (filters[key]) query[USAGE_QUERY[key]] = filters[key];
  });
  if (USAGE_SORT_KEYS.indexOf(sort.sort) !== -1 && sort.sort !== DEFAULT_USAGE_SORT.sort) query[USAGE_QUERY.sort] = sort.sort;
  if (sort.desc === false) query[USAGE_QUERY.ascending] = '1';
  if (isNumber(source.page) && source.page > 1) query[USAGE_QUERY.page] = String(Math.floor(source.page));
  if (PAGE_SIZES.indexOf(source.pageSize) !== -1 && source.pageSize !== USAGE_PAGE_SIZE) query[USAGE_QUERY.pageSize] = String(source.pageSize);
  return query;
};

export const withUsageQuery = (query, usageQuery) => {
  const source = objectOf(query);
  const next = {};
  Object.keys(source).forEach((key) => {
    if (USAGE_QUERY_KEYS.indexOf(key) === -1) next[key] = source[key];
  });
  return Object.assign(next, objectOf(usageQuery));
};

export const readUsageQuery = (storage) => {
  if (!storage) return null;
  try {
    const saved = JSON.parse(storage.getItem(USAGE_STORAGE_KEY) || 'null');
    return saved && typeof saved === 'object' && !Array.isArray(saved) && hasUsageQuery(saved) ? saved : null;
  } catch (e) {
    return null;
  }
};

export const writeUsageQuery = (storage, usageQuery) => {
  if (!storage) return;
  try {
    storage.setItem(USAGE_STORAGE_KEY, JSON.stringify(objectOf(usageQuery)));
  } catch (e) {
    return;
  }
};

export const statusOptions = () => Object.keys(STATUS_INFO).map((key) => ({ value: key, label: STATUS_INFO[key].label }));

const userLabel = (user) => cleanText(user.name, 120) || cleanText(user.email, 254) || cleanText(user.masterUserId, 80) || EMPTY;

export const mergeUsers = (known, byUser) => {
  const list = listOf(known).slice();
  listOf(byUser).forEach((raw) => {
    const user = objectOf(raw);
    const id = cleanText(user.masterUserId, 80);
    if (!USER_ID.test(id)) return;
    const option = { value: id, label: userLabel(user), email: cleanText(user.email, 254) };
    const index = list.map((item) => item.value).indexOf(id);
    if (index === -1) list.push(option);
    else list[index] = option;
  });
  return list.sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
};

const rateText = (value) => (isNumber(value) ? formatPercent(value, 1) : EMPTY);

export const usageCards = (raw) => {
  const totals = objectOf(raw);
  const questions = numberOr(totals.questions, 0);
  const feedback = numberOr(totals.feedbackCount, 0);
  return [
    {
      key: 'questions', label: 'Perguntas', infoKey: 'assistant.usage.questions', value: formatInteger(questions),
      sub: plural(numberOr(totals.conversations, 0), 'conversa', 'conversas'),
    },
    { key: 'users', label: 'Pessoas', infoKey: 'assistant.usage.users', value: formatInteger(numberOr(totals.users, 0)), sub: 'que perguntaram no período' },
    {
      key: 'cost', label: 'Custo total', infoKey: 'assistant.usage.cost', value: formatMoney(numberOr(totals.costUsd, 0), 'USD'),
      sub: formatMoney(numberOr(totals.costBrl, 0), 'BRL'),
    },
    {
      key: 'avgCost', label: 'Custo médio por pergunta', infoKey: 'assistant.usage.avgCost',
      value: questions ? formatMoney(numberOr(totals.avgCostUsd, 0), 'USD') : EMPTY,
      sub: questions ? formatMoney(numberOr(totals.avgCostBrl, 0), 'BRL') : 'sem perguntas',
    },
    {
      key: 'input', label: 'Tokens de entrada', infoKey: 'assistant.usage.inputTokens', value: formatInteger(numberOr(totals.inputTokens, 0)),
      sub: formatInteger(numberOr(totals.cachedInputTokens, 0)) + ' em cache',
    },
    {
      key: 'output', label: 'Tokens de saída', infoKey: 'assistant.usage.outputTokens', value: formatInteger(numberOr(totals.outputTokens, 0)),
      sub: 'resposta e pedidos de ferramenta',
    },
    {
      key: 'latency', label: 'Tempo médio', infoKey: 'assistant.usage.avgLatency', value: questions ? latencyText(numberOr(totals.avgLatencyMs, 0)) : EMPTY,
      sub: questions ? 'p95 ' + latencyText(numberOr(totals.p95LatencyMs, 0)) : 'sem perguntas',
    },
    {
      key: 'errors', label: 'Com erro', infoKey: 'assistant.usage.errorRate', value: rateText(totals.errorRate),
      sub: isNumber(totals.errorRate) ? 'das perguntas do período' : 'sem perguntas',
    },
    {
      key: 'useful', label: 'Avaliadas como úteis', infoKey: 'assistant.usage.usefulRate', value: rateText(totals.usefulRate),
      sub: plural(feedback, 'avaliação', 'avaliações'),
    },
  ];
};

export const dayList = (byDay) => listOf(byDay)
  .map((raw) => objectOf(raw))
  .filter((day) => parseIsoDate(day.date))
  .sort((a, b) => (a.date < b.date ? 1 : (a.date > b.date ? -1 : 0)));

export const userList = (byUser) => listOf(byUser)
  .map((raw) => objectOf(raw))
  .filter((user) => cleanText(user.masterUserId, 80));

export const agentList = (byAgent) => listOf(byAgent)
  .map((raw) => objectOf(raw))
  .filter((agent) => cleanText(agent.agent, 40));

export const dayRows = (byDay) => dayList(byDay)
  .map((day) => ({
    key: day.date,
    dateText: formatDateFull(day.date),
    questionsText: formatInteger(numberOr(day.questions, 0)),
    usersText: formatInteger(numberOr(day.users, 0)),
    inputText: formatInteger(numberOr(day.inputTokens, 0)),
    cachedText: formatInteger(numberOr(day.cachedInputTokens, 0)),
    outputText: formatInteger(numberOr(day.outputTokens, 0)),
    costUsdText: formatMoney(numberOr(day.costUsd, 0), 'USD'),
    costBrlText: formatMoney(numberOr(day.costBrl, 0), 'BRL'),
  }));

export const dayBars = (byDay) => listOf(byDay)
  .map((raw) => objectOf(raw))
  .filter((day) => parseIsoDate(day.date))
  .sort((a, b) => (a.date < b.date ? -1 : (a.date > b.date ? 1 : 0)))
  .map((day) => {
    const questions = numberOr(day.questions, 0);
    return {
      key: day.date,
      label: formatDateFull(day.date),
      value: questions,
      valueText: plural(questions, 'pergunta', 'perguntas') + ' · ' + formatMoney(numberOr(day.costUsd, 0), 'USD'),
    };
  });

export const userRows = (byUser) => userList(byUser)
  .map((user) => ({
    key: cleanText(user.masterUserId, 80),
    id: cleanText(user.masterUserId, 80),
    name: senderName(user.name),
    email: cleanText(user.email, 254),
    questionsText: formatInteger(numberOr(user.questions, 0)),
    costText: formatMoney(numberOr(user.costUsd, 0), 'USD'),
    costBrlText: formatMoney(numberOr(user.costBrl, 0), 'BRL'),
    avgCostText: formatMoney(numberOr(user.avgCostUsd, 0), 'USD'),
    lastText: stampText(user.lastAskedAt),
  }));

export const agentRows = (byAgent) => agentList(byAgent)
  .map((agent) => ({
    key: cleanText(agent.agent, 40),
    label: agentLabel(agent.agent, agent.label),
    questionsText: formatInteger(numberOr(agent.questions, 0)),
    costText: formatMoney(numberOr(agent.costUsd, 0), 'USD'),
    avgCostText: formatMoney(numberOr(agent.avgCostUsd, 0), 'USD'),
    latencyText: latencyText(numberOr(agent.avgLatencyMs, 0)),
    usefulText: rateText(agent.usefulRate),
  }));

export const agentPathText = (requested, used) => {
  const from = agentLabel(requested);
  const to = agentLabel(used);
  if (!cleanText(requested, 40) || requested === used) return to;
  return from + ' → ' + to;
};

const unverifiedText = (count) => (count > 0 ? plural(count, 'número não conferido', 'números não conferidos') : '');

export const usageItems = (items) => listOf(items)
  .map((raw) => objectOf(raw))
  .filter((item) => isNumber(item.interactionId) && item.interactionId > 0)
  .map((item) => {
    const unverified = numberOr(item.unverifiedNumbers, 0);
    const inputText = formatInteger(numberOr(item.inputTokens, 0));
    const cachedText = formatInteger(numberOr(item.cachedInputTokens, 0));
    const outputText = formatInteger(numberOr(item.outputTokens, 0));
    const modelCallsText = plural(numberOr(item.modelCalls, 0), 'chamada ao modelo', 'chamadas ao modelo');
    const toolCallsText = plural(numberOr(item.toolCalls, 0), 'consulta ao PI', 'consultas ao PI');
    const asked = toInstant(item.askedAt);
    return {
      key: 'i' + item.interactionId,
      id: Math.floor(item.interactionId),
      askedAtText: stampText(item.askedAt),
      askedDateText: asked ? formatDateFull(asked) : EMPTY,
      askedTimeText: asked ? formatTime(asked) : '',
      userName: cleanText(item.userName, 200) || '(sem nome)',
      userEmail: cleanText(item.userEmail, 254),
      question: shorten(item.question, USAGE_QUESTION_PREVIEW),
      questionFull: cleanText(item.question, 2000),
      headline: shorten(inlineSegments(item.headline).map((segment) => segment.text).join(''), USAGE_HEADLINE_PREVIEW),
      agentText: agentPathText(item.agentRequested, item.agentUsed),
      status: statusInfo(item.status),
      modelCallsText: modelCallsText,
      toolCallsText: toolCallsText,
      inputText: inputText,
      cachedText: cachedText,
      outputText: outputText,
      tokensDetail: inputText + ' de entrada (' + cachedText + ' em cache) · ' + outputText + ' de saída · ' + modelCallsText + ' · ' + toolCallsText,
      totalText: formatInteger(numberOr(item.totalTokens, numberOr(item.inputTokens, 0) + numberOr(item.outputTokens, 0))),
      costUsdText: formatMoney(numberOr(item.costUsd, 0), 'USD'),
      costBrlText: formatMoney(numberOr(item.costBrl, 0), 'BRL'),
      latencyText: latencyText(item.latencyMs),
      feedback: feedbackInfo(item.feedback),
      unverified: unverified,
      unverifiedText: unverifiedText(unverified),
    };
  });

export const usageTotal = (rows) => {
  const source = objectOf(rows);
  return isNumber(source.total) && source.total >= 0 ? Math.floor(source.total) : listOf(source.items).length;
};

export const priceText = (pricing) => {
  const source = objectOf(pricing);
  if (!isNumber(source.inputPer1MUsd) || !isNumber(source.cachedInputPer1MUsd) || !isNumber(source.outputPer1MUsd)) return '';
  const parts = [
    formatMoney(source.inputPer1MUsd, 'USD') + '/1M entrada',
    formatMoney(source.cachedInputPer1MUsd, 'USD') + '/1M em cache',
    formatMoney(source.outputPer1MUsd, 'USD') + '/1M saída',
  ];
  if (isNumber(source.usdBrlRate)) parts.push('câmbio ' + formatMoney(source.usdBrlRate, 'BRL') + ' (configurado no servidor)');
  return 'gpt-4.1-mini Global: ' + parts.join(' · ');
};

const hashShort = (value) => {
  const text = cleanText(value, 80);
  return text ? text.substring(0, 12) : '';
};

export const prettyJson = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') {
    const text = value.trim();
    if (text.charAt(0) === '{' || text.charAt(0) === '[') {
      try {
        return JSON.stringify(JSON.parse(text), null, 2);
      } catch (e) {
        return value;
      }
    }
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch (e) {
    return String(value);
  }
};

const callNumbers = (calls) => calls.map((call) => '#' + call.index);

const joinPt = (items) => (items.length <= 1 ? items.join('') : items.slice(0, -1).join(', ') + ' e ' + items[items.length - 1]);

export const transcriptMarks = (messages, modelCalls) => {
  const calls = listOf(modelCalls).map((raw) => objectOf(raw)).filter((call) => isNumber(call.index) && isNumber(call.inputMessageCount));
  const answers = calls.filter((call) => call.purpose === 'answer');
  const marked = answers.length ? answers : calls.filter((call) => call.purpose === 'router');
  return listOf(messages).map((message, position) => {
    const marks = [];
    const outputs = marked.filter((call) => call.inputMessageCount === position);
    if (outputs.length) marks.push({ key: 'o', text: 'Saída da chamada ' + joinPt(callNumbers(outputs)), tone: 'info' });
    const inputs = marked.filter((call) => call.inputMessageCount > position);
    if (inputs.length) {
      marks.push({ key: 'i', text: (inputs.length === 1 ? 'Entrada da chamada ' : 'Entrada das chamadas ') + joinPt(callNumbers(inputs)), tone: 'muted' });
    }
    return marks;
  });
};

const toolCallsOf = (list) => listOf(list).map((raw, index) => {
  const call = objectOf(raw);
  return {
    key: 'tc' + index,
    id: cleanText(call.id, 80),
    name: cleanText(call.name, 80) || EMPTY,
    argumentsText: prettyJson(typeof call.arguments === 'string' ? call.arguments : '') || '{}',
  };
});

export const transcriptRows = (messages, modelCalls, systemPromptHash) => {
  const list = listOf(messages).map((raw) => objectOf(raw));
  const marks = transcriptMarks(list, modelCalls);
  const mainHash = cleanText(systemPromptHash, 80).toLowerCase();
  return list.map((message, index) => {
    const role = cleanText(message.role, 20);
    const hash = cleanText(message.promptHash, 80).toLowerCase();
    let note = '';
    if (hash && (message.content === null || message.content === undefined)) {
      note = hash === mainHash
        ? 'Prompt de sistema ' + hashShort(hash) + ': texto completo em "Prompt de sistema" logo abaixo.'
        : 'Prompt gravado pelo hash ' + hashShort(hash) + ' (texto em pa.assistant_prompt).';
    }
    return {
      key: 'm' + index,
      index: index,
      role: role,
      roleLabel: TRANSCRIPT_ROLES[role] || role || EMPTY,
      content: typeof message.content === 'string' ? prettyJson(message.content) : '',
      note: note,
      toolCalls: toolCallsOf(message.toolCalls),
      toolCallId: cleanText(message.toolCallId, 80),
      marks: marks[index],
    };
  });
};

const modelCallRows = (list) => listOf(list).map((raw, position) => {
  const call = objectOf(raw);
  const finish = cleanText(call.finishReason, 30);
  return {
    key: 'mc' + position,
    index: isNumber(call.index) ? call.index : position,
    purposeText: PURPOSE_LABELS[call.purpose] || cleanText(call.purpose, 20) || EMPTY,
    promptHash: hashShort(call.promptHash),
    startedText: stampText(call.startedAt),
    latencyText: latencyText(call.latencyMs),
    messagesText: isNumber(call.inputMessageCount) ? formatInteger(call.inputMessageCount) : EMPTY,
    inputText: formatInteger(numberOr(call.inputTokens, 0)),
    cachedText: formatInteger(numberOr(call.cachedInputTokens, 0)),
    outputText: formatInteger(numberOr(call.outputTokens, 0)),
    finishText: finish ? (FINISH_LABELS[finish] ? FINISH_LABELS[finish] + ' (' + finish + ')' : finish) : EMPTY,
    toolCallsText: formatInteger(numberOr(call.toolCallCount, 0)),
    costText: formatMoneyExact(numberOr(call.costUsd, 0), 'USD'),
    model: cleanText(call.model, 80) || EMPTY,
    errorCode: cleanText(call.errorCode, 60),
  };
});

const flagText = (value) => {
  if (value === true) return 'sim';
  if (value === false) return 'não';
  return EMPTY;
};

const toolCallRows = (list) => listOf(list).map((raw, position) => {
  const call = objectOf(raw);
  return {
    key: 'tl' + position,
    callText: '#' + (isNumber(call.modelCallIndex) ? call.modelCallIndex : '?') + '.' + (isNumber(call.index) ? call.index : position),
    tool: cleanText(call.tool, 60) || EMPTY,
    toolCallId: cleanText(call.toolCallId, 80),
    endpoint: cleanText(call.endpoint, 100),
    argumentsText: call.arguments === null || call.arguments === undefined ? '' : prettyJson(call.arguments),
    status: toolStatusInfo(call.status),
    latencyText: latencyText(call.latencyMs),
    charsText: formatInteger(numberOr(call.resultChars, 0)),
    tokensText: formatInteger(numberOr(call.resultTokensEstimate, 0)),
    truncatedText: flagText(call.truncated),
    rowsText: isNumber(call.rows) ? formatInteger(call.rows) : EMPTY,
    asOfText: call.asOf ? stampText(call.asOf) : EMPTY,
    partialText: flagText(call.isPartial),
    dataText: call.asOf ? 'dados de ' + stampText(call.asOf) + (call.isPartial === true || call.isPartial === false ? ' · parcial ' + flagText(call.isPartial) : '') : '',
    errorMessage: cleanText(call.errorMessage, 400),
  };
});

const metaRow = (key, label, value) => ({ key: key, label: label, value: value || EMPTY });

export const usageDetail = (raw) => {
  const source = objectOf(raw);
  const id = isNumber(source.interactionId) ? Math.floor(source.interactionId) : 0;
  const messages = Array.isArray(source.messages) ? source.messages : null;
  const purged = source.transcriptPurged === true;
  const feedback = feedbackInfo(source.feedback);
  const used = cleanText(source.agentUsed, 40);
  const confidence = isNumber(source.routeConfidence) ? ' (confiança ' + formatPercent(source.routeConfidence * 100, 0) + ')' : '';
  const errorCode = cleanText(source.errorCode, 60);
  const errorMessage = cleanText(source.errorMessage, 400);
  const unverified = numberOr(source.unverifiedNumbers, 0);
  const answer = source.answer && typeof source.answer === 'object' ? normalizeBody(source.answer) : null;
  const question = cleanText(source.question, 2000);
  const agentText = agentPathText(source.agentRequested, used);
  const exportLines = ['Pergunta nº ' + id + (question ? ': "' + question + '"' : '')];
  if (answer && answer.periodText) exportLines.push(answer.periodText);
  exportLines.push('Agente: ' + agentText);
  return {
    id: id,
    title: 'Pergunta #' + id,
    askedAtText: stampText(source.askedAt),
    finishedAtText: stampText(source.finishedAt),
    userName: cleanText(source.userName, 200) || '(sem nome)',
    userEmail: cleanText(source.userEmail, 254),
    userId: cleanText(source.masterUserId, 80),
    question: question,
    status: statusInfo(source.status),
    agentText: agentText,
    routeText: routeText(agentLabel(used), source.routeMethod) + confidence,
    conversationText: (isGuid(source.conversationId) ? source.conversationId : EMPTY) + (isNumber(source.turn) ? ' · rodada ' + source.turn : ''),
    answer: answer,
    exportContext: { screen: USAGE_SCREEN, filters: exportLines, period: answer ? answer.period : null, envelope: null },
    modelCallSource: listOf(source.modelCalls).map((raw) => objectOf(raw)),
    toolCallSource: listOf(source.toolCalls).map((raw) => objectOf(raw)),
    trace: normalizeTrace(source.trace),
    warnings: warningItems(source.warnings),
    unverifiedText: unverifiedText(unverified),
    totals: [
      Object.assign(metaRow('tokens', 'Tokens', formatInteger(numberOr(source.inputTokens, 0)) + ' de entrada (' +
        formatInteger(numberOr(source.cachedInputTokens, 0)) + ' em cache) · ' + formatInteger(numberOr(source.outputTokens, 0)) + ' de saída · ' +
        formatInteger(numberOr(source.totalTokens, 0)) + ' no total'), { infoKey: 'assistant.usage.totalTokens' }),
      Object.assign(metaRow('cost', 'Custo da pergunta', formatMoneyExact(numberOr(source.costUsd, 0), 'USD') + ' · ' +
        formatMoneyExact(numberOr(source.costBrl, 0), 'BRL')), { infoKey: 'assistant.usage.cost' }),
      Object.assign(metaRow('latency', 'Tempo', latencyText(source.latencyMs) + ' no total · modelo ' + latencyText(source.modelLatencyMs) +
        ' · ferramentas ' + latencyText(source.toolLatencyMs)), { infoKey: 'assistant.usage.latency' }),
    ],
    meta: [
      metaRow('model', 'Modelo', cleanText(source.model, 80)),
      metaRow('deployment', 'Implantação', cleanText(source.deployment, 80)),
      metaRow('host', 'Endpoint', cleanText(source.endpointHost, 200)),
      metaRow('prompt', 'Versão do prompt', cleanText(source.promptVersion, 60)),
      metaRow('knowledge', 'Versão do conhecimento', cleanText(source.knowledgeVersion, 60)),
      metaRow('price', 'Versão do preço', cleanText(source.priceVersion, 80)),
      metaRow('rate', 'Câmbio usado', isNumber(source.usdBrlRate) ? formatMoney(source.usdBrlRate, 'BRL') + ' por US$ 1' : ''),
      metaRow('hash', 'Hash do prompt de sistema', cleanText(source.systemPromptHash, 80)),
    ],
    errorText: errorCode ? errorCode + (errorMessage ? ': ' + errorMessage : '') : errorMessage,
    feedback: feedback,
    feedbackComment: cleanText(source.feedbackComment, 500),
    feedbackAtText: source.feedbackAt ? stampText(source.feedbackAt) : '',
    contextText: source.context && typeof source.context === 'object' ? prettyJson(source.context) : '',
    modelCalls: modelCallRows(source.modelCalls).map((call) => Object.assign({}, call, { modelShown: call.model !== EMPTY && call.model !== cleanText(source.model, 80) })),
    toolCalls: toolCallRows(source.toolCalls),
    purged: purged,
    transcriptText: purged ? PURGED_TEXT : (messages ? '' : NO_TRANSCRIPT_TEXT),
    transcript: messages ? transcriptRows(messages, source.modelCalls, source.systemPromptHash) : [],
    systemPrompt: typeof source.systemPrompt === 'string' ? source.systemPrompt : '',
    systemPromptHash: hashShort(source.systemPromptHash),
    costLine: costPair(numberOr(source.costUsd, 0), numberOr(source.costBrl, null)),
  };
};

export const detailParam = (query) => {
  const raw = objectOf(query).detalhe;
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (typeof value !== 'string' || !/^\d{1,18}$/.test(value)) return 0;
  const id = Number(value);
  return id > 0 && isFinite(id) ? id : 0;
};

export const REVENUE_DENIED_TEXT = 'Sem acesso a Receita e oportunidades: esta pergunta é desse agente (403).';

export const usageError = (error, action) => {
  const info = classifyError(error);
  const body = objectOf(error && error.response && error.response.data);
  if (info.status === 403 && body.code === 'assistant_revenue_denied') {
    return { kind: 'forbidden', status: 403, message: action === 'detail' ? REVENUE_DENIED_TEXT : 'Sem acesso a Receita e oportunidades (403).', retryable: false };
  }
  if (info.kind === 'invalid') return Object.assign({}, info, { message: 'Filtro inválido para o consumo (400). Revise o período (até 400 dias, sem datas futuras).', retryable: false });
  if (info.kind === 'disabled') {
    return { kind: 'disabled', status: 404, message: action === 'detail' ? 'Pergunta não encontrada (404).' : 'O assistente está desligado neste ambiente (404).', retryable: false };
  }
  if (info.kind === 'forbidden') return { kind: 'forbidden', status: info.status, message: 'Sem permissão (' + info.status + '). É necessário um usuário operador da plataforma.', retryable: false };
  if (info.kind === 'session') return { kind: 'session', status: null, message: 'Saia e entre novamente no Master para autenticar na API v2.', retryable: false };
  return {
    kind: 'failed',
    status: info.status,
    message: 'Não foi possível ' + (action === 'detail' ? 'carregar a pergunta' : 'carregar o consumo da IA') + (info.status ? ' (' + info.status + ')' : '') + '.',
    retryable: true,
  };
};



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/piAssistantUsageModel.js